<script lang="ts" setup>
  import type { Plan, PlanPaymentMethod, SubscriberUsingAttemptRequest, SubscriptionAttempt } from '@/types'
  import radioBoxMarded from '@/assets/icons/radiobox-marked.svg'
  import radioBoxBlank from '@/assets/icons/radiobox-blank.svg'
  import { getSubPaymentMethodName, handlePaymentErrors } from '@/utils/payments.ts'
  import backArrowIcon from '@assets/icons/consultation/back-arrow.svg'

  const props = defineProps<{
    plans: Plan[],
    preSelectedPlanPaymentMethod?: PlanPaymentMethod
  }>()

  const { t } = useI18n()
  const trackStore = useTrackStore()
  const errors = ref<Record<number, string>>({})
  const activePlanPaymentMethod = ref<PlanPaymentMethod | undefined>(props.preSelectedPlanPaymentMethod)
  const snackbar = useSnackbar()


  const emit = defineEmits<{
    'back': [],
    'create-payment': [attempt: SubscriptionAttempt, payload: SubscriberUsingAttemptRequest]
    'retry-attempt': [attempt: SubscriptionAttempt]
  }>()

  const handleError = (error: unknown, planPaymentMethod?: PlanPaymentMethod) => {
    const errorMessage = handlePaymentErrors(error)

    if (planPaymentMethod?.id) {
      errors.value[planPaymentMethod?.id] = errorMessage
    }

    trackStore.track('payment_failed_presented', {
      payment_type: planPaymentMethod?.sub_payment_method_id,
      error: errorMessage,
      version: trackStore.subscriptionVersion,
    })

    snackbar.showSnackbar({
      color: 'error',
      text: errorMessage,
      timeout: 5000,
    })

    console.error('Payment attempt error:', error)
  }

  const clearError = (planPaymentMethodId: number) => {
    delete errors.value[planPaymentMethodId]
  }

  const handlePaymentCompleted = (attempt: SubscriptionAttempt, payload: SubscriberUsingAttemptRequest) => {
    clearError(attempt.plan_payment_method_id)
    emit('create-payment', attempt, payload)
  }

  const handleRetryAttempt = (attempt: SubscriptionAttempt) => {
    clearError(attempt.plan_payment_method_id)
    emit('retry-attempt', attempt)
  }

  const handlePaymentError = (error: unknown, planPaymentMethod: PlanPaymentMethod) => {
    handleError(error, planPaymentMethod)
  }
</script>

<template>
  <v-card
    class="subscription-expansion-panels d-flex flex-column bg-background"
    flat
    :height="$vuetify.display.mdAndDown ? '95dvh' : '95dvh'"
    :rounded="$vuetify.display.mdAndDown? '0' : 'xl'"
  >
    <v-card-title class="font-weight-bold pa-0 mb-6" style="font-size: 1.125rem;">
      <div class="d-flex align-center ga-2">
        <v-btn
          class="p-0 m-0"
          :ripple="false"
          size="32"
          variant="text"
          @click="emit('back')"
        >
          <v-icon :icon="backArrowIcon" size="24" />
        </v-btn>

        <i18n-t keypath="subscription.list_title" />
      </div>
    </v-card-title>

    <v-expansion-panels
      id="subscription-payment-methods"
      v-model="activePlanPaymentMethod"
      class="ga-2 overflow-y-scroll hide-scrollbar"
      flat
      :focusable="false"
      :style="$vuetify.display.mdAndDown ? 'max-height: auto;' : 'max-height: 50dvh;'"
    >
      <template v-for="plan in plans" :key="plan.id">
        <v-expansion-panel
          v-for="planPaymentMethod in plan.plan_payment_methods"
          :key="planPaymentMethod.id"
          bg-color="grey-800"
          class="ma-0"
          :value="planPaymentMethod"
        >
          <v-expansion-panel-title class="pa-4">
            <div class="d-flex ga-4 align-center">
              <div class="pa-1 bg-white rounded-md">
                <v-img
                  height="20"
                  :src="planPaymentMethod?.sub_payment_method?.media?.url"
                  width="46"
                />
              </div>
              <p class="text-grey-50 text-body-1">
                {{ planPaymentMethod.sub_payment_method?.name }}
              </p>
            </div>
            <template #actions="{ expanded }">
              <div class="text-surface">
                <v-radio
                  color="grey-50"
                  density="compact"
                  :false-icon="radioBoxBlank"
                  :model-value="expanded"
                  :true-icon="radioBoxMarded"
                />
              </div>
            </template>
          </v-expansion-panel-title>

          <v-expansion-panel-text>
            <v-alert
              v-if="errors[planPaymentMethod.id]"
              class="mb-4"
              closable
              type="error"
              variant="tonal"
              @click:close="clearError(planPaymentMethod.id)"
            >
              {{ errors[planPaymentMethod.id] }}
            </v-alert>

            <suspense>
              <payment-form
                :key="planPaymentMethod.id"
                :plan-payment-method="planPaymentMethod"
                @payment-completed="handlePaymentCompleted"
                @payment-error="handlePaymentError($event, planPaymentMethod)"
                @retry-attempt="handleRetryAttempt"
              />

              <template #fallback>
                <payment-skeleton-loader
                  :type="getSubPaymentMethodName(planPaymentMethod.sub_payment_method_id ?? 1)"
                />
              </template>
            </suspense>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </template>
    </v-expansion-panels>
    <v-card
      v-if="activePlanPaymentMethod"
      class="px-6 pt-6 pb-0 mt-auto bg-background payment-summary-box rounded-0 flex-shrink-0"
      flat
    >
      <v-card-text class="pa-0 mt-auto">
        <div class="d-flex justify-space-between align-center ga-2 max-w-lg">
          <span class="text-grey-400"> {{ t('sina_pro') }} </span>
          <bdi>
            {{ (activePlanPaymentMethod?.price_local).toFixed(2) }}
            {{
              t('plan_price_details', {
                price: t(`subscription.currencies.${activePlanPaymentMethod.currency}`),
                duration: t(`subscription.${activePlanPaymentMethod.plan?.is_recurring ? 'is_recurring' : 'not_recurring'}.durations.${activePlanPaymentMethod.duration}`),
              })
            }}
          </bdi>
        </div>
        <div
          v-if="activePlanPaymentMethod.coupons?.[0].type == 'percentage' && activePlanPaymentMethod.coupons?.[0].pivot "
          class="d-flex justify-space-between align-center ga-2 max-w-lg mt-2"
        >
          <span class="text-grey-400">
            {{
              t('coupon_discount_onetime', {
                code: activePlanPaymentMethod.coupons[0].code
              })
            }}
          </span>
          <bdi>
            {{
              t('coupon_discount_onetime_details', {
                discount: (activePlanPaymentMethod?.price_local - activePlanPaymentMethod.coupons?.[0].pivot.price_local).toFixed(2),
                currency: t(`subscription.currencies.${activePlanPaymentMethod.currency}`),
                code: activePlanPaymentMethod.coupons[0].code,
              })
            }}
          </bdi>
        </div>
        <div
          class="d-flex justify-space-between align-center ga-2 max-w-lg pt-4 mt-3 border-t-sm border-opacity-50 border-gray-300"
        >
          {{ t('total_charges') }}
          <bdi>
            {{
              (activePlanPaymentMethod.coupons?.[0].pivot?.price_local || activePlanPaymentMethod?.price_local).toFixed(2)
            }}
            {{ t(`subscription.currencies.${activePlanPaymentMethod.currency}`) }}
          </bdi>
        </div>
      </v-card-text>
    </v-card>
  </v-card>
</template>

<style lang="scss" scoped>
@use '@/styles/settings' as v;
@use 'sass:map';

#subscription-payment-methods :deep(.v-expansion-panel) {
  border-radius: map.get(v.$rounded, 'sm') !important;
}

#subscription-payment-methods :deep(.v-expansion-panel-text__wrapper) {
  padding: 1rem !important;
}

.payment-summary-box {
  box-shadow: 0 -10px 24px 0 rgba(0, 0, 0, 0.05);
}

.hide-scrollbar {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Hide scrollbar for Chrome, Safari and Opera */
  }
}

</style>
